import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { changePasswordSchema } from '../../lib/validators/authSchemas';
import { useChangePasswordMutation, useLogoutMutation } from '../../features/auth/authApi';
import { selectCurrentUser, selectMustChangePassword } from '../../features/auth/authSlice';
import { authErrorMessage } from '../../lib/authErrorMessage';
import PasswordInput from '../../components/ui/PasswordInput';
import Button from '../../components/ui/Button';

/**
 * Reached two ways: forced (ProtectedRoute redirects here whenever
 * must_change_password is set — every other protected endpoint 403s until
 * it's cleared) and voluntary (a Topbar "Change password" link for anyone).
 * Same form either way; only the framing copy and the post-success
 * destination differ. Per the docs this also revokes every other session
 * for the account, so we log the *local* session out too rather than
 * silently keep using a token the server may treat as stale.
 */
export default function ChangePassword() {
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const forced = useSelector(selectMustChangePassword);
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [logout] = useLogoutMutation();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({ resolver: zodResolver(changePasswordSchema) });

  const onSubmit = async ({ new_password_confirmation, ...values }) => {
    try {
      await changePassword(values).unwrap();
      // Revokes every refresh token for this account, including the one
      // this tab is holding — re-authenticate cleanly rather than keep
      // running on a session the server has already invalidated.
      await logout(undefined).unwrap().catch(() => null);
      navigate('/login', { replace: true, state: { passwordReset: true } });
    } catch (err) {
      const status = err?.status;
      setError('root', {
        message: authErrorMessage(err, {
          fallback: status === 403 ? 'Current password is incorrect.' : 'Could not change password.',
        }),
      });
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">
        {forced ? 'Set a new password' : 'Change your password'}
      </h1>
      <p className="text-sm text-foreground-muted mb-6">
        {forced
          ? `${user?.first_name ? `Hi ${user.first_name}, y` : 'Y'}our account was created with a temporary password — set your own before continuing.`
          : 'This signs you out of every other device.'}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <PasswordInput
          label="Current password"
          autoComplete="current-password"
          error={errors.current_password?.message}
          {...register('current_password')}
        />
        <PasswordInput
          label="New password"
          autoComplete="new-password"
          error={errors.new_password?.message}
          {...register('new_password')}
        />
        <PasswordInput
          label="Confirm new password"
          autoComplete="new-password"
          error={errors.new_password_confirmation?.message}
          {...register('new_password_confirmation')}
        />

        {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}

        <Button type="submit" className="w-full" isLoading={isLoading}>
          Change password
        </Button>
      </form>
    </div>
  );
}
